<template>
    <div class="card" v-if="loaded">

        <div class="card-header border-0">
            <div class="row">
                <div class="col-4">
                    <h3 class="mb-0 text-uppercase">
                        {{ $t('common.issues') }}
                    </h3>
                </div>
                <div class="col-8 text-right">

                    <base-button type="secondary" size="sm" @click="clearFilters" v-if="hasFilters">
                        {{ $t('common.clear-filters') }}
                    </base-button>

                    <base-button type="primary" size="sm" @click="openFilters()">
                        {{ $t('common.more-filters') }}
                    </base-button>
                </div>
            </div>
        </div>

        <div class="pt-2">
            
            <table-control :paging="ctrl.paging" :search="true" v-model="search" ref="search"></table-control>

            <el-table class="table-responsive table-flush"
                    header-row-class-name="thead-light"
                    :data="ctrl.rows"
                    @sort-change="sortChange"
                    @filter-change="tableFiltersChange"
                    ref="table">

                <el-table-column :label="$t('common.cohort') + ' / ' + $t('common.degree')">
                    <template v-slot="{row}">
                        <h4 class="mb-0">
                            <a href="#" @click.prevent="filterByCohort(row.cohort)">
                                {{ row.cohort.name }}
                            </a>
                            /
                            <a href="#" @click.prevent="filterByDegree(row.degree)">
                                {{ row.degree.name }}
                            </a>
                        </h4>
                    </template>
                </el-table-column>

                <el-table-column :label="$t('common.simulations')">
                    <template v-slot="{row}">

                        <div class="p-1">
                            <base-button v-for="simulation in simulations(row)" :key="simulation.id"
                                type="secondary" size="sm" @click="features.openSimulationIssues(row, simulation)" class="mr-2"
                            >
                                {{ simulation.name }}
                            </base-button>
                        </div>

                    </template>
                </el-table-column>
            </el-table>

            <table-pagination :paging="ctrl.paging"></table-pagination>
        </div>

        <portal to="modals">

            <form-modal v-model="ctrl.modals.filters"
                :title="$t('common.filters')"
            >
                <registration-filters
                    :modal="ctrl.modals.filters"
                    :value="filters"
                    @input="globalFiltersChange"
                >
                </registration-filters>

            </form-modal>

        </portal>
    </div>
</template>

<script>
    import CrudController from '@trax/classes/CrudController'
    import CrudFiltering from '../../../classes/CrudFiltering'
    import RegistrationFilters from './RegistrationFilters'
    import IssuesFeatures from './IssuesFeatures'

    export default {

        components: {
            RegistrationFilters
        },

        data() {
            let features = new IssuesFeatures(this.$router, this.$auth)
            let filtering = new CrudFiltering('dashboard_issues')
            return {
                loaded: false,
                features,
                filtering,
                ctrl: new CrudController({
                    endpoint: '/socrates/api/front/dashboard/data/issues/registrations',
                    mapData: features.mapData,
                    unmapData: features.unmapData,
                    modals: ['filters'],
                    paging: {
                        perPage: 10,
                        perPageOptions: [10, 25, 50],
                        total: 0
                    },
                    query: {
                        sort: ['-id'],
                        relations: ['cohort', 'degree'],
                        accessors: ['simulations'],
                    },
                    callbacks: {
                        listed: this.listed,
                    }
                }),
                search: '',
                filters: {},
            }
        },

        created() {

            this.filtering.init(this)

            this.ctrl.query.filters = this.getFilters()

            this.ctrl.init(this, 'ctrl').then(resp => {
                this.loaded = true
            })
        },

        mounted() {
            if (this.filters.simpleSearch) {
                this.$refs.search.setFilter(this.filters.simpleSearch)
            }
        },

        watch: {
            search(val) {
                if (val === '') {
                    delete this.filters.simpleSearch
                } else {
                    this.filters.simpleSearch = val
                }
                this.filters = {...this.filters}    // Clone to force reactivity
                this.reload()
            }
        },

        computed: {

            empty() {
                return this.ctrl.rows.length == 0 && !this.search
            },

            hasFilters() {
                // First, remove null filters.
                Object.keys(this.filters).forEach(key => {
                    if (!this.filters[key]) {
                        delete this.filters[key]
                    }
                })

                return Object.keys(this.filters).length > 0 || this.ctrl.query.sort[0] != '-id'
            },
        },

        methods: {

            simulations(registration) {
                // return registration.simulations.filter(simulation => !simulation.disabled)
                // We have access to all the simulations.
                return registration.simulations
            },

            sortChange(data) {
                this.filtering.sortChange(data, ['-id'])
            },

            tableFiltersChange(data) {
                this.filtering.filterChange(data)
            },

            globalFiltersChange(data) {
                this.filters = { ...this.filters, ...data}
                this.reload()
            },

            filterByDegree(degree) {
                this.filters.degree_id = degree.id
                this.filters = {...this.filters}    // Clone to force reactivity
                this.reload()
            },

            filterByCohort(cohort) {
                this.filters.cohort_id = cohort.id
                this.filters = {...this.filters}    // Clone to force reactivity
                this.reload()
            },

            clearFilters() {
                this.filtering.clearFilters({}, ['-id'])
            },

            listed() {
                this.filtering.saveUiConfig()
            },

            getFilters() {
                let filters = {$and: []}
                
                // Search.
                if (this.filters.simpleSearch) {
                    filters.$and.push({ simpleSearch: this.filters.simpleSearch})
                }
                
                // Addtional filters.
                if (this.filters.cohort_id) {
                    filters.$and.push(
                        {cohort_id: this.filters.cohort_id}
                    )
                }
                if (this.filters.degree_id) {
                    filters.$and.push(
                        {degree_id: this.filters.degree_id}
                    )
                }

                if (filters.$and.length == 0) {
                    filters = {}
                }
                return filters
            },

            reload() {
                this.ctrl.filter(
                    this.getFilters()
                )
            },

            openFilters() {
                this.ctrl.modals.open('filters').then(filters => {
                    this.reload()
                })
            },
        }
    }
</script>

<style scoped>

    .card >>> th,
    .card >>> td {
        padding-right: 0;
    }
    
</style>
